import {useEffect, useMemo, useRef, useState} from 'react';
import ProfileLinks from './ProfileLinks';
import BanStatusDisplay from './BanStatusDisplay';
import FriendsList from './FriendsList';
import Badge from './Badge';
import type {ProfileSummary, FriendData} from '../types/steam';
import {formatDateWithAge, getSmurfMatch} from '../utils/dateUtils';

interface ProfileSectionProps {
    summary: ProfileSummary | null;
    friends: FriendData[];
    isLoading: boolean;
}

function ProfileSection({summary, friends, isLoading}: ProfileSectionProps) {
    const [isRecentNamesOpen, setIsRecentNamesOpen] = useState(false);
    const recentNamesRef = useRef<HTMLDivElement>(null);
    const smurfMatch = useMemo(() => {
        if (!summary) {
            return null;
        }

        return friends
            .map((friend) => getSmurfMatch(friend, summary))
            .filter((match): match is NonNullable<typeof match> => match !== null)
            .sort((a, b) => b.strength - a.strength)[0] ?? null;
    }, [friends, summary]);

    useEffect(() => {
        const closeOnOutsideClick = (event: PointerEvent) => {
            if (!recentNamesRef.current?.contains(event.target as Node)) {
                setIsRecentNamesOpen(false);
            }
        };
        document.addEventListener('pointerdown', closeOnOutsideClick);
        return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
    }, []);

    const formatSummaryPercentage = (count: number, total: number) =>
        total > 0 ? `(${Math.round((count / total) * 100)}%)` : '';

    const generateTextSummary = (summary: ProfileSummary): string => {
        let text: string = `${summary.id64}:`;

        // Ban status
        if (summary.vacBanned || summary.gameBans > 0) {
            const banParts: string[] = [];
            if (summary.vacBanned && summary.vacBans > 0) {
                banParts.push(`${summary.vacBans} VAC ban${summary.vacBans !== 1 ? 's' : ''}`);
            } else if (summary.vacBanned) {
                banParts.push('VAC banned');
            }
            if (summary.gameBans > 0) {
                banParts.push(`${summary.gameBans} Game Ban${summary.gameBans !== 1 ? 's' : ''}`);
            }
            text += ' ' + banParts.join(', ') + ' on record';
        } else {
            text += ' No Bans on record';
        }

        // Account age
        if (summary.timeCreated) {
            const date: Date = new Date(summary.timeCreated * 1000);
            const formattedDate: string = date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});
            text += `, created ${formattedDate.replace(',', '')}`;
        }

        // Friends info
        const friendBanParts = [];
        if (summary.friendVacCount > 0) {
            friendBanParts.push(
                `${summary.friendVacCount} VAC ban${summary.friendVacCount !== 1 ? 's' : ''}` +
                formatSummaryPercentage(summary.friendVacCount, summary.friendCount),
            );
        }
        if (summary.friendGameCount > 0) {
            friendBanParts.push(
                `${summary.friendGameCount} Game Ban${summary.friendGameCount !== 1 ? 's' : ''}` +
                formatSummaryPercentage(summary.friendGameCount, summary.friendCount),
            );
        }

        text += `. Friends: ${friendBanParts.length > 0 ? friendBanParts.join(', ') : 'No bans listed'}`;

        return text;
    };

    const copyToClipboard = async () => {
        if (!summary) return;

        const text = generateTextSummary(summary);
        try {
            // Use modern Clipboard API
            // Note: This requires a secure context (HTTPS or localhost)
            await navigator.clipboard.writeText(text);
            alert('Summary copied to clipboard!');
        } catch (err) {
            console.error('Failed to copy:', err);
            alert('Failed to copy to clipboard. Make sure you are using HTTPS or localhost.');
        }
    };

    const formatAccountAge = (timestamp?: number) => {
        if (!timestamp) return '—';
        return formatDateWithAge(timestamp);
    };

    const formatPlaytime = (minutes?: number) => {
        if (minutes === undefined || minutes === 0) return '—';
        const hours = Math.floor(minutes / 60);
        if (hours < 1) return `${minutes} min`;
        return `${hours.toLocaleString()} hrs`;
    };

    return (
        <section className="mt-6 card p-4 sm:p-6 pb-0 sm:pb-0 overflow-hidden">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <a
                        href={summary?.profileUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="friend-name text-2xl font-semibold truncate hover:underline"
                    >
                        {summary?.persona || 'Account Summary'}
                    </a>
                    {summary && (
                        <div ref={recentNamesRef} className="recent-names-dropdown relative shrink-0">
                            <button
                                type="button"
                                onClick={() => setIsRecentNamesOpen((open) => !open)}
                                className="recent-names-toggle text-muted hover:text-gray-700 dark:hover:text-gray-200"
                                title="Recent names"
                                aria-label="Show recent names"
                                aria-expanded={isRecentNamesOpen}
                            >
                                <svg className={`recent-names-arrow w-3 h-3 ${isRecentNamesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M19 9l-7 7-7-7"/>
                                </svg>
                            </button>
                            {isRecentNamesOpen && <div className="recent-names-menu absolute left-0 top-full z-10 mt-2 min-w-48 rounded-lg border p-2 text-sm shadow-lg">
                                {summary.recentNames && summary.recentNames.length > 0 ? (
                                    <ul className="space-y-1">
                                        {summary.recentNames.map((name) => (
                                            <li key={name} className="text-secondary whitespace-nowrap">
                                                {name}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="text-muted whitespace-nowrap">No recent names</div>
                                )}
                            </div>}
                        </div>
                    )}
                </div>
                {summary && <ProfileLinks steamId64={summary.id64}/>}
            </div>

            {/* Badge row directly below title */}
            {summary && (
                <div className="flex gap-1 flex-wrap mt-2">
                    {smurfMatch && (
                        <Badge
                            variant={smurfMatch.type === 'direct' ? 'smurf' : 'smurf-recent'}
                            strength={smurfMatch.strength}
                            title={smurfMatch.type === 'direct'
                                ? 'Has friend(s) with current names similar to this profile'
                                : 'Has friend(s) matching one of this profile’s recent names'}
                        >
                            {smurfMatch.type === 'direct' ? 'Smurf? · current name' : 'Smurf? · recent name'}
                        </Badge>
                    )}
                    <BanStatusDisplay summary={summary}/>
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="spinner"/>
                </div>
            ) : (
                <>
                    <div className="relative">
                        <dl className="grid grid-cols-2 gap-3 text-sm mt-3">
                            <div>
                                <dt className="text-muted">Account Age</dt>
                                <dd>{summary ? formatAccountAge(summary.timeCreated) : '—'}</dd>
                            </div>
                            <div>
                                <dt className="text-muted">CS2 Playtime</dt>
                                <dd>{summary ? formatPlaytime(summary.cs2PlaytimeMinutes) : '—'}</dd>
                            </div>
                        </dl>
                        {summary && (
                            <button
                                type="button"
                                onClick={copyToClipboard}
                                title="Copy text summary to clipboard"
                                className="absolute bottom-0 right-0 copy-button"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Friends List Section */}
                    {summary && (
                        <div className="mt-6 pt-6 border-gray-200 dark:border-gray-700">
                            <FriendsList friends={friends} mainProfile={summary}/>
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

export default ProfileSection;
